import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import BackButton from "./BackButton";
import LoadingSpinner from "./LoadingSpinner";

// Stripe & Google Maps
const stripePromise = (async () => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key || !key.startsWith("pk_")) {
    console.error("Invalid Stripe key format");
    return null;
  }
  return loadStripe(key);
})();

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const MAP_LIBRARIES = ["places"];

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const CheckoutForm = ({ planDetails, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [billingDetails, setBillingDetails] = useState({
    name: "",
    email: "",
    address: {
      line1: "",
      city: "",
      state: "",
      postal_code: "",
    },
  });

  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place) {
        const { formatted_address, geometry, address_components } = place;
        const updatedDetails = {
          line1: formatted_address || "",
          city: "",
          state: "",
          postal_code: "",
        };

        address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality"))
            updatedDetails.city = component.long_name;
          if (types.includes("administrative_area_level_1"))
            updatedDetails.state = component.long_name;
          if (types.includes("postal_code"))
            updatedDetails.postal_code = component.long_name;
        });

        setBillingDetails((prev) => ({
          ...prev,
          address: { ...prev.address, ...updatedDetails },
        }));
      }
    }
  };

  const calculateDiscountedPrice = () => {
    if (!appliedCoupon) return parseFloat(planDetails.price);
    const originalPrice = parseFloat(planDetails.price);

    if (appliedCoupon.percent_off) {
      return originalPrice - (originalPrice * appliedCoupon.percent_off) / 100;
    } else if (appliedCoupon.amount_off) {
      const discountAmount = appliedCoupon.amount_off / 100;
      return Math.max(0, originalPrice - discountAmount);
    }

    return originalPrice;
  };

  const validateCouponWithStripe = async (couponId) => {
    try {
      setCouponLoading(true);
      setCouponError("");
      const response = await axios.post(
        `${config.API_BASE_URL}/subscription/validate-coupon`,
        {
          coupon_id: couponId,
          plan_id: planDetails.plan_id,
        }
      );

      if (response.data.valid) {
        return response.data.coupon;
      } else {
        throw new Error(response.data.error || "Invalid coupon");
      }
    } catch (error) {
      throw new Error(
        error.response?.data?.error || "Failed to validate coupon"
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    setCouponError("");
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    try {
      const couponData = await validateCouponWithStripe(couponCode.trim());
      setAppliedCoupon(couponData);
      setCouponError("");
    } catch (error) {
      setCouponError(error.message);
      setAppliedCoupon(null);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    // Validate all required billing fields
    const { name, email, address } = billingDetails;
    const { line1, city, state, postal_code } = address;

    if (
      !name.trim() ||
      !email.trim() ||
      !line1.trim() ||
      !city.trim() ||
      !state.trim() ||
      !postal_code.trim()
    ) {
      setError("Please fill out all billing details before proceeding.");
      return;
    }

    if (error) {
      elements.getElement("card").focus();
      return;
    }

    if (!cardComplete) {
      setError("Please complete your card details");
      return;
    }

    setProcessing(true);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: billingDetails,
      });

      if (error) {
        setError(error.message);
        setProcessing(false);
        return;
      }

      const userId = sessionStorage.getItem("user_id");
      const response = await axios.post(
        `${config.API_BASE_URL}/subscription/create-subscription`,
        {
          user_id: userId,
          plan_id: planDetails.plan_id,
          payment_method_id: paymentMethod.id,
          coupon_id: appliedCoupon?.id || null,
        }
      );

      onSuccess(response.data);
    } catch (err) {
      setError(
        err.response?.data?.error || "An error occurred. Please try again."
      );
      setProcessing(false);
    }
  };

  const originalPrice = parseFloat(planDetails.price);
  const finalPrice = calculateDiscountedPrice();
  const savings = originalPrice - finalPrice;

  const getCouponDescription = () => {
    if (!appliedCoupon) return "";
    let description = "";

    if (appliedCoupon.percent_off) {
      description = `${appliedCoupon.percent_off}% off`;
    } else if (appliedCoupon.amount_off) {
      description = `$${(appliedCoupon.amount_off / 100).toFixed(2)} off`;
    } else {
      description = "Discount applied";
    }

    if (appliedCoupon.metadata?.applicable_product_id) {
      description += ` (for specific plan only)`;
    }

    return description;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Coupon Section */}
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-2 h-6 bg-green-500 rounded-full"></div>
          <h3 className="text-xl font-semibold text-gray-900">Coupon Code</h3>
        </div>

        {!appliedCoupon ? (
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), handleApplyCoupon())
              }
              disabled={couponLoading}
            />
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={couponLoading}
              className={`px-6 py-3 text-white rounded-lg font-semibold transition-all duration-300 ${
                couponLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-lg transform hover:scale-105"
              } focus:outline-none focus:ring-4 focus:ring-green-300 focus:ring-opacity-50`}
            >
              {couponLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Applying...
                </div>
              ) : (
                "Apply"
              )}
            </button>
          </div>
        ) : (
          <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-green-800 font-medium">
                  Coupon "{appliedCoupon.id}" applied!
                </p>
                <p className="text-green-600 text-sm">
                  {getCouponDescription()}
                  {appliedCoupon.name && ` - ${appliedCoupon.name}`}
                </p>
                {appliedCoupon.valid === false && (
                  <p className="text-red-600 text-sm">
                    This coupon has expired
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                className="text-red-600 hover:text-red-800 text-sm font-medium underline transition-colors duration-200"
              >
                Remove
              </button>
            </div>
          </div>
        )}

        {couponError && (
          <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-lg text-sm">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {couponError}
            </div>
          </div>
        )}
      </div>

      {/* Price Summary */}
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
          <h3 className="text-xl font-semibold text-gray-900">Order Summary</h3>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-700">Plan: {planDetails.name}</span>
            <span className="font-medium">${originalPrice.toFixed(2)}/month</span>
          </div>

          {appliedCoupon && savings > 0 && (
            <>
              <div className="flex justify-between items-center py-2 text-green-600">
                <span>Discount ({appliedCoupon.id}):</span>
                <span className="font-medium">-${savings.toFixed(2)}</span>
              </div>
              <hr className="my-3 border-gray-200" />
            </>
          )}

          <div className="flex justify-between items-center py-3 border-t border-gray-200">
            <span className="text-lg font-bold text-gray-900">Total:</span>
            <div className="text-right">
              {appliedCoupon && savings > 0 && (
                <span className="line-through text-gray-500 text-sm mr-2 block">
                  ${originalPrice.toFixed(2)}
                </span>
              )}
              <span className="text-2xl font-bold text-gray-900">
                ${finalPrice.toFixed(2)}<span className="text-lg font-normal text-gray-600">/month</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Details */}
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
          <h3 className="text-xl font-semibold text-gray-900">Billing Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Full Name
            </label>
            <input
              id="name"
              type="text"
              placeholder="Jane Doe"
              required
              value={billingDetails.name}
              onChange={(e) => {
                setBillingDetails({ ...billingDetails, name: e.target.value });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="jane.doe@example.com"
              required
              value={billingDetails.email}
              onChange={(e) => {
                setBillingDetails({ ...billingDetails, email: e.target.value });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>

        {/* Address Autocomplete */}
        <div className="mt-4">
          <label
            htmlFor="address"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Address
          </label>
          {isLoaded && (
            <Autocomplete
              onLoad={setAutocomplete}
              onPlaceChanged={handlePlaceSelect}
            >
              <input
                id="address"
                type="text"
                placeholder="Search your address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={billingDetails.address.line1}
                onChange={(e) =>
                  setBillingDetails({
                    ...billingDetails,
                    address: {
                      ...billingDetails.address,
                      line1: e.target.value,
                    },
                  })
                }
              />
            </Autocomplete>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              City
            </label>
            <input
              id="city"
              type="text"
              placeholder="San Francisco"
              required
              value={billingDetails.address.city}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: { ...billingDetails.address, city: e.target.value },
                });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <div>
            <label
              htmlFor="state"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              State
            </label>
            <input
              id="state"
              type="text"
              placeholder="CA"
              required
              value={billingDetails.address.state}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: { ...billingDetails.address, state: e.target.value },
                });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <div>
            <label
              htmlFor="postal_code"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              ZIP / Postal Code
            </label>
            <input
              id="postal_code"
              type="text"
              placeholder="94103"
              required
              value={billingDetails.address.postal_code}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: {
                    ...billingDetails.address,
                    postal_code: e.target.value,
                  },
                });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-2 h-6 bg-indigo-500 rounded-full"></div>
          <h3 className="text-xl font-semibold text-gray-900">Payment Method</h3>
        </div>

        <div className="mb-6">
          <label
            htmlFor="card"
            className="block text-sm font-medium text-gray-700 mb-3"
          >
            Card Details
          </label>
          <div className="p-4 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200">
            <CardElement
              id="card"
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => {
                setError(e.error ? e.error.message : "");
                setCardComplete(e.complete);
              }}
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            type="submit"
            disabled={processing || !stripe || !cardComplete}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform ${
              processing || !stripe || !cardComplete
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:scale-105"
            } focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50`}
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing Payment...
              </div>
            ) : (
              `Complete Payment - $${finalPrice.toFixed(2)}`
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

const PaymentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (location.state && location.state.planDetails) {
      setPlanDetails(location.state.planDetails);
      setLoading(false);
    } else {
      setError("No plan selected. Please select a subscription plan first.");
      setTimeout(() => {
        navigate("/select-subscription");
      }, 2000);
    }
  }, [location, navigate]);

  const handlePaymentSuccess = (data) => {
    if (data.subscription_id) {
      sessionStorage.setItem("subscription_id", data.subscription_id);
    }
    alert("Subscription created successfully!");
    navigate("/dashboard");
  };

  if (loading && !error) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate("/select-subscription")}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 hover:shadow-lg transform hover:scale-105 transition-all duration-300"
            >
              Back to Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <BackButton />
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="bg-white rounded-full p-3 shadow-lg inline-flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Complete Your Subscription
          </h1>
          <p className="text-xl text-gray-600">
            You're subscribing to the <span className="font-semibold text-blue-600">{planDetails.name}</span> plan
          </p>
        </div>

        <Elements stripe={stripePromise}>
          <CheckoutForm
            planDetails={planDetails}
            onSuccess={handlePaymentSuccess}
          />
        </Elements>

        {/* Security Notice */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center text-gray-500 text-sm">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Your payment information is secure and encrypted
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;